import {
  PromotionType,
  TRACKING_PAGE_GRID_CELL_SIZE,
  TRACKING_PAGE_GRID_GAP,
  TrackingPagePromotionBlock,
} from "@redotech/order-tracking-common/tracking-page-definition";
import { useRequiredContext } from "@redotech/react-util/context";
import {
  SUNDAYRED_TEAMS,
  TAYLORMADE_TEAMS,
} from "@redotech/redo-integrations-definitions-common/taylormade/constants";
import { TrackableType } from "@redotech/redo-order-tracking-trackable-common/trackable";
import { ok } from "@redotech/util/fp/coerced-result";
import { memo, useMemo } from "react";
import { Flex } from "../../flex";
import ImageIcon from "../../icon-old/image.svg";
import { Text } from "../../text";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../context/tracking-page-context";
import * as promotionCss from "./promotion.module.css";
import { getBlockStyles } from "./shared/block-styles";
import { BlockWrapper } from "./shared/block-wrapper";

export const Promotion = memo(function Promotion({
  url,
  imageUrl,
  altText,
  promotionType,
  size,
  fillBlock,
  borderColor,
}: TrackingPagePromotionBlock) {
  const { order, productUpsell, pageSettings, environment, subscribeUrl } =
    useRequiredContext(TrackingPageContext);

  const blockStyles = getBlockStyles(pageSettings);
  const isLoading = !order;

  // Calculate recommended dimensions based on block size
  const recommendedWidth =
    size.width * TRACKING_PAGE_GRID_CELL_SIZE +
    (size.width - 1) * TRACKING_PAGE_GRID_GAP;
  const recommendedHeight =
    size.height * TRACKING_PAGE_GRID_CELL_SIZE +
    (size.height - 1) * TRACKING_PAGE_GRID_GAP;

  const baseStoreUrl = useMemo(() => {
    if (!url) {
      return url;
    }

    const isCanadianOrder = order?.shippingAddress?.countryCode === "CA";

    if (
      order?.teamId &&
      TAYLORMADE_TEAMS.includes(order.teamId) &&
      isCanadianOrder
    ) {
      return url.replace("taylormadegolf.com", "taylormadegolf.ca");
    }
    if (
      order?.teamId &&
      SUNDAYRED_TEAMS.includes(order.teamId) &&
      isCanadianOrder
    ) {
      return url.replace("sundayred.com", "sundayred.ca");
    }
    return url;
  }, [order?.teamId, url, order?.shippingAddress?.countryCode]);

  const handleClick = async () => {
    if (
      order &&
      baseStoreUrl &&
      productUpsell &&
      environment === TrackingPageEnvironment.CUSTOMER_PORTAL
    ) {
      await productUpsell.logPageClick({
        trackableId: order.internalMongoId,
        trackableType: TrackableType.ORDER,
        eventType: "ad",
        url: baseStoreUrl,
        image: imageUrl,
      });
    }
  };

  // For subscribe promotions, use subscribeUrl; otherwise add utm params to url
  const urlResult = baseStoreUrl
    ? ok(baseStoreUrl).map((baseStoreUrl) => new URL(baseStoreUrl))
    : { ok: true, value: null };
  const urlWithUtm = urlResult.ok === true ? urlResult.value : null;

  if (urlWithUtm && order && promotionType !== PromotionType.SUBSCRIBE) {
    urlWithUtm.searchParams.append("utm_source", "redo");
    urlWithUtm.searchParams.append("utm_medium", "order_tracking");
    urlWithUtm.searchParams.append("utm_content", "promotion");
    urlWithUtm.searchParams.append("utm_term", order.internalMongoId);
  }

  const showBorder = !!imageUrl && borderColor !== "transparent";
  const useCustomBorderColor = showBorder && borderColor !== undefined;

  const card = (
    <Flex
      borderColor={showBorder && !useCustomBorderColor ? "primary" : undefined}
      borderStyle={showBorder && !useCustomBorderColor ? "solid" : undefined}
      borderWidth={showBorder && !useCustomBorderColor ? "1px" : undefined}
      dir="column"
      gap="none"
      height={!imageUrl || fillBlock ? "full" : undefined}
      onClick={
        environment === TrackingPageEnvironment.BUILDER
          ? undefined
          : handleClick
      }
      style={{
        ...blockStyles,
        backgroundColor: "transparent",
        ...(useCustomBorderColor ? { border: `1px solid ${borderColor}` } : {}),
      }}
    >
      {!imageUrl && environment === TrackingPageEnvironment.BUILDER && (
        <Flex
          align="center"
          dir="column"
          gap="xs"
          height="full"
          justify="center"
          p="xl"
        >
          <ImageIcon height={32} width={32} />
          <Text fontSize="sm" textColor="secondary">
            Add image
          </Text>
          <Text fontSize="xs" textColor="secondary">
            {recommendedWidth} × {recommendedHeight} px
          </Text>
        </Flex>
      )}
      {imageUrl && (
        <img
          alt={altText}
          src={imageUrl}
          style={{
            width: "100%",
            height: "100%",
            objectFit: fillBlock ? "cover" : undefined,
            borderRadius: blockStyles.borderRadius,
          }}
        />
      )}
    </Flex>
  );

  return (
    <BlockWrapper
      environment={environment}
      loading={isLoading}
      pageSettings={pageSettings}
      transparentBackground
      withBorder={false}
    >
      {order && (
        <>
          {/* If promotion type is subscribe, handle differently (similar to AdCard) */}
          {promotionType === PromotionType.SUBSCRIBE ? (
            // In builder mode, don't make it a clickable link
            environment === TrackingPageEnvironment.BUILDER ? (
              card
            ) : (
              <a
                className={promotionCss.promotionLink}
                href={subscribeUrl}
                rel="noreferrer"
                target="_blank"
              >
                {card}
              </a>
            )
          ) : urlWithUtm ? (
            // In builder mode, don't make it a clickable link
            environment === TrackingPageEnvironment.BUILDER ? (
              card
            ) : (
              <a
                className={promotionCss.promotionLink}
                href={urlWithUtm.toString()}
                rel="noreferrer"
                target="_blank"
              >
                {card}
              </a>
            )
          ) : (
            card
          )}
        </>
      )}
    </BlockWrapper>
  );
});
